import React, { useState, useEffect } from 'react';
import { Outlet, history } from 'ice';
import { Spin, Modal, Watermark } from 'antd';
import { store as starkStore } from '@ice/stark-data';
import store from '@/store';
import HeaderLayout from './components/Layouts/HeaderLayout';
import { ensureAMapSdkLoaded } from '@/utils/amapSdk';
import { ensureHereSdkLoaded } from '@/utils/hereMapSdk';


import styles from './global.less';

const HERE_TENANT_GROUP_IDS = new Set<string>(['ag', 'ub']);

function resolveMapProvider(groupId: string | undefined | null): 'amap' | 'here' {
  if (groupId && HERE_TENANT_GROUP_IDS.has(groupId)) return 'here';
  return 'amap';
}

export default function Entry(props: {
  children: React.ReactNode;
  pathname: string;
  appLeave: { path: string };
  appEnter: { path: string };
}) {
  const { pathname, children, appLeave, appEnter } = props;
  const [loading, setLoading] = useState(true);
  const [isFetchedUserInfo, setIsFetchedUserInfo] = useState(false);
  const [userInfo, setUserInfo] = useState(store.getState().user);

  useEffect(() => {
    // 统一预加载 AMap SDK,供子应用复用。
    // 失败不阻塞主应用渲染,子应用可自行决定兜底策略(如提示/刷新)。
    ensureAMapSdkLoaded().catch((e) => {
      console.warn('AMap SDK preload failed:', e);
    });
  }, []);

  // 根据当前租户选择地图 provider,并在切到 Here 租户时按需加载 Here SDK。
  // 默认 fallback 为 amap,行为与改造前一致。
  useEffect(() => {
    const onGroupChange = (groupId: string | undefined) => {
      const provider = resolveMapProvider(groupId);
      console.log('[PROVIDER][host] onGroupChange', { groupId, provider });
      starkStore.set('currentMapProvider', provider);
      if (provider === 'here') {
        ensureHereSdkLoaded().catch((e) => {
          console.warn('Here SDK load failed:', e);
        });
      }
    };
    // force=true 让订阅时立刻用当前值触发一次,无需再手动 syncProvider
    // @ts-ignore: starkStore 类型未暴露 on/off,运行时支持
    starkStore.on?.('currentGroup', onGroupChange, true);
    console.log('[PROVIDER][host] subscribed to currentGroup (force=true)');
    return () => {
      // @ts-ignore
      starkStore.off?.('currentGroup', onGroupChange);
    };
  }, []);

  const handleJumpTo = (path: string) => {
    console.log('handleJumpTo', path, history, "appLeave:", appLeave, "appEnter:", appEnter);
    history?.push(path);
  }
  const handleLogout = () => {
    window.location.replace('/api/perms/logout');
  }
  // 订阅 store 变化
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      setUserInfo(store.getState().user);
    });
    return unsubscribe;
  }, []);

  // 页面加载逻辑
  useEffect(() => {
    const loadUserData = async () => {
      // 如果是登录页面，不需要加载用户数据
      if (pathname === '/login' || isFetchedUserInfo) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await store.dispatch.user.asyncGetUserInfo();
        setLoading(false);
        setIsFetchedUserInfo(true);
      } catch (error) {
        console.error('获取用户信息失败:', error);
        // 跳转到登录页面
        window.location.href = '/login';
      }
    };
    if (userInfo.userId) {
      setLoading(false);
      return;
    } else {
      loadUserData();
    }
  }, [pathname, isFetchedUserInfo]);

  useEffect(() => {
    console.log('== app leave ==', appLeave);
  }, [appLeave]);

  useEffect(() => {
    console.log('== app enter ==', appEnter);
  }, [appEnter]);

  // 登录页面直接渲染，不需要布局
  if (pathname === '/login' || pathname === '/login-success' || pathname === '/exception/403' || pathname === '/exception/404') {
    return (
      <>
        {children || <Outlet />}
      </>
    );
  }

  // 加载中状态
  if (loading) {
    return (
      <div className={styles.globalLoading}>
        <Spin size="large" spinning={true}>
          <div style={{ width: '100%', height: '100vh' }} />
        </Spin>
      </div>
    );
  }

  const watermarkContent = userInfo.userName && userInfo.mobile
    ? `${userInfo.userName} ${userInfo.mobile.slice(-4)}`
    : 'Loading...';

  return (
    <>
      <Watermark
        content={watermarkContent}
        font={{ color: 'rgba(0, 0, 0, 0.05)' }}
      >
        <HeaderLayout
          pathname={pathname}
          handleLogout={handleLogout}
          handleJumpTo={handleJumpTo}
          userInfo={userInfo}>
          {children}
        </HeaderLayout>
      </Watermark>
    </>
  );
}
